type BodyStyleSnapshot = {
  overflow: string
  position: string
  top: string
  width: string
  paddingRight: string
}

let lockCount = 0
let snapshot: BodyStyleSnapshot | null = null
let lockedScrollY = 0

const getScrollbarWidth = (): number =>
  window.innerWidth - document.documentElement.clientWidth

const isGuideTourStepCardTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('[data-testid="guide-tour-step-card"]') !== null

const preventBackgroundScroll = (event: Event): void => {
  if (isGuideTourStepCardTarget(event.target)) {
    return
  }

  event.preventDefault()
}

export const isGuideTourPageScrollLocked = (): boolean => lockCount > 0

export const getGuideTourLockedScrollY = (): number =>
  isGuideTourPageScrollLocked() ? lockedScrollY : window.scrollY

/** Updates the simulated page scroll while the tour keeps the body fixed. */
export const setGuideTourLockedScrollY = (nextScrollY: number): void => {
  const targetScrollY = Math.max(0, nextScrollY)

  if (!isGuideTourPageScrollLocked()) {
    window.scrollTo({ top: targetScrollY, left: 0, behavior: 'auto' })
    return
  }

  lockedScrollY = targetScrollY
  document.body.style.top = `-${lockedScrollY}px`
}

export const GUIDE_TOUR_PAGE_SCROLL_UNLOCK_POLL_MS = 16

export const GUIDE_TOUR_PAGE_SCROLL_UNLOCK_TIMEOUT_MS = 1_000

export const waitForGuideTourPageScrollUnlock = (): Promise<void> => {
  if (typeof window === 'undefined' || !isGuideTourPageScrollLocked()) {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    const startedAt = Date.now()

    const finish = () => {
      window.clearInterval(pollId)
      window.clearTimeout(timeoutId)
      resolve()
    }

    const pollId = window.setInterval(() => {
      if (!isGuideTourPageScrollLocked()) {
        finish()
      }
    }, GUIDE_TOUR_PAGE_SCROLL_UNLOCK_POLL_MS)

    const timeoutId = window.setTimeout(() => {
      finish()
    }, GUIDE_TOUR_PAGE_SCROLL_UNLOCK_TIMEOUT_MS)

    if (Date.now() - startedAt > GUIDE_TOUR_PAGE_SCROLL_UNLOCK_TIMEOUT_MS) {
      finish()
    }
  })
}

export const lockGuideTourPageScroll = (): (() => void) => {
  if (lockCount === 0) {
    const { style } = document.body
    lockedScrollY = window.scrollY
    const scrollbarWidth = getScrollbarWidth()

    snapshot = {
      overflow: style.overflow,
      position: style.position,
      top: style.top,
      width: style.width,
      paddingRight: style.paddingRight,
    }

    style.overflow = 'hidden'
    style.position = 'fixed'
    style.top = `-${lockedScrollY}px`
    style.width = '100%'

    if (scrollbarWidth > 0) {
      style.paddingRight = `${scrollbarWidth}px`
    }

    document.addEventListener('wheel', preventBackgroundScroll, {
      passive: false,
    })
    document.addEventListener('touchmove', preventBackgroundScroll, {
      passive: false,
    })
  }

  lockCount += 1

  return () => {
    lockCount = Math.max(0, lockCount - 1)

    if (lockCount > 0) {
      return
    }

    document.removeEventListener('wheel', preventBackgroundScroll)
    document.removeEventListener('touchmove', preventBackgroundScroll)

    if (snapshot) {
      const { style } = document.body
      style.overflow = snapshot.overflow
      style.position = snapshot.position
      style.top = snapshot.top
      style.width = snapshot.width
      style.paddingRight = snapshot.paddingRight
    }

    window.scrollTo(0, lockedScrollY)

    snapshot = null
    lockedScrollY = 0
  }
}

/** Test helper to reset module lock state between tests. */
export const resetGuideTourPageScrollLockForTests = (): void => {
  lockCount = 0
  snapshot = null
  lockedScrollY = 0
}

const parseLockedBodyScrollY = (topStyle: string): number => {
  if (!topStyle || topStyle === '0px') {
    return 0
  }

  const parsed = Number.parseInt(topStyle, 10)

  return Number.isFinite(parsed) ? Math.abs(parsed) : 0
}

/**
 * Restores body scroll when a previous tour session left `position: fixed`
 * without an active lock (e.g. hot reload or crash mid-step).
 */
export const releaseOrphanedGuideTourPageScrollLock = (): void => {
  if (typeof window === 'undefined' || lockCount > 0) {
    return
  }

  const { style } = document.body
  const isOrphanedLock =
    style.position === 'fixed' &&
    (style.overflow === 'hidden' || style.top.startsWith('-'))

  if (!isOrphanedLock) {
    return
  }

  const scrollY = parseLockedBodyScrollY(style.top)

  style.overflow = ''
  style.position = ''
  style.top = ''
  style.width = ''
  style.paddingRight = ''

  window.scrollTo(0, scrollY)
}
